import React from 'react';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faRotateLeft, faTriangleExclamation } from '@fortawesome/free-solid-svg-icons';

/**
 * Small pieces the Paper Pricing screens share (ledger row 132): the status dot,
 * the "not set" dash, the load-failure banner and a labelled form field.
 */

/** Green = Confirmed / Active, amber = Manual, grey = nothing there. Never colour alone: the word is always shown. */
export const StatusDot = ({ tone, children, title }) => (
    <span className={`pp-status pp-status--${tone}`} title={title}>
        <span className={`pp-dot pp-dot--${tone}`} aria-hidden="true" />
        {children}
    </span>
);

export const ActiveStatus = ({ active }) => (
    <StatusDot tone={active ? 'green' : 'grey'}>{active ? 'Active' : 'Inactive'}</StatusDot>
);

/** An optional field left empty. A dash, so an empty cell never reads as "still loading". */
export const Dash = () => <span className="pp-muted" aria-label="Not set">—</span>;

export const textOrDash = (value) => (value === null || value === undefined || value === '' ? <Dash /> : value);

/** A load that failed says so, in the server's words, with a way to try again. */
export const LoadProblem = ({ message, onRetry }) => (
    <div className="pp-alert pp-alert--error pp-load-problem" role="alert">
        <FontAwesomeIcon icon={faTriangleExclamation} className="pp-alert-icon" />
        <span className="pp-alert-text">{message}</span>
        {onRetry ? (
            <button type="button" className="pp-btn pp-btn--secondary pp-btn--small" onClick={onRetry}>
                <FontAwesomeIcon icon={faRotateLeft} /> Try again
            </button>
        ) : null}
    </div>
);

/**
 * A labelled box. Its refusal (or hint) is tied to the box itself, so a screen
 * reader reads the server's sentence out when the box is reached, not only
 * whoever can see the red text under it.
 */
export const FormField = ({ label, htmlFor, required = false, hint, error, children }) => {
    const errorId = error ? `${htmlFor}-error` : undefined;
    const hintId = hint && !error ? `${htmlFor}-hint` : undefined;
    const control = React.isValidElement(children)
        ? React.cloneElement(children, {
            'aria-invalid': error ? true : undefined,
            'aria-describedby': errorId || hintId,
        })
        : children;
    return (
        <div className={`pp-field ${error ? 'pp-field--error' : ''}`}>
            <label className="pp-field-label" htmlFor={htmlFor}>
                {label}
                {required ? <span className="pp-required" aria-hidden="true"> *</span> : null}
            </label>
            {control}
            {error ? <div id={errorId} className="pp-field-error">{error}</div> : null}
            {hintId ? <div id={hintId} className="pp-field-hint">{hint}</div> : null}
        </div>
    );
};
